import { UserType, normalizeUserType } from "./constants";

const getBrowserLocale = () => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return undefined;
};

/** IANA timezone for API payloads (e.g. heatmap bucketing). */
const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** Offset string for custom range filters (e.g. +05:30). */
const getBrowserTimeZoneOffset = () => {
  const now = new Date();
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const h = String(Math.floor(abs / 60)).padStart(2, "0");
  const m = String(abs % 60).padStart(2, "0");
  return `${sign}${h}:${m}`;
};

/** True when an ISO-like string has no Z or numeric offset (server timestamps are UTC). */
const hasExplicitTimeZone = (s) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);

/**
 * Parse API/ClickHouse timestamps for display. Naive "YYYY-MM-DD HH:MM:SS" and
 * timezone-less ISO strings are treated as UTC (LDCP-96); then Intl formats in
 * the user's local timezone.
 */
const parseDate = (date) => {
  if (date == null || date === "") return null;
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof date === "number") {
    const ms = date < 1e12 ? date * 1000 : date;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof date === "string") {
    const s = date.trim();
    if (!s) return null;
    // ClickHouse HTTP: 'YYYY-MM-DD HH:MM:SS[.fraction]' (UTC storage, no suffix)
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) {
      const iso = s.replace(" ", "T");
      const d = new Date(hasExplicitTimeZone(iso) ? iso : `${iso}Z`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // ISO datetime without offset — server UTC
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s) && !hasExplicitTimeZone(s)) {
      const d = new Date(`${s}Z`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const EMPTY_CELL = "—";

function displayCell(value) {
  if (value == null) return EMPTY_CELL;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : EMPTY_CELL;
  const s = String(value).trim();
  return s === "" ? EMPTY_CELL : s;
}

/** Single display helper: browser locale + local timezone + short TZ abbrev (LDCP-96). */
const formatDate = (date) => {
  const d = parseDate(date);
  if (!d) return "";
  return new Intl.DateTimeFormat(getBrowserLocale(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    // timeZoneName: "short",
  }).format(d);
};

const formatDateOnly = (date) => {
  const d = parseDate(date);
  if (!d) return "";
  return new Intl.DateTimeFormat(getBrowserLocale(), {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

const formatTimeOnly = (date) => {
  const d = parseDate(date);
  if (!d) return "";
  return new Intl.DateTimeFormat(getBrowserLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
};

const getHumanTimeLength = (d) => {
  d = Number(d);
  let h = Math.floor(d / 3600);
  let m = Math.floor((d % 3600) / 60);
  let s = Math.floor((d % 3600) % 60);

  let sResult = "";
  let hDisplay = h > 0 ? h + (h === 1 ? " hour" : " hours") : "";
  sResult += hDisplay;
  let mDisplay = m > 0 ? m + (m === 1 ? " minute" : " minutes") : "";
  if (0 < mDisplay.length) {
    if (0 < sResult.length) sResult += ", ";
    sResult += mDisplay;
  }
  let sDisplay = s > 0 ? s + (s === 1 ? " second" : " seconds") : "";
  if (0 < sDisplay.length) {
    if (0 < sResult.length) sResult += ", ";
    sResult += sDisplay;
  }
  return sResult;
};

const formatBytes = (bytes, decimals = 2) => {
  if (!bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const formatNumbers = (bytes, decimals = 2) => {
  if (!bytes) return "0";
  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["", "K", "M", "G", "T", "P", "E", "Z", "Y"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const formatHttpResponseCode = (res_code) => {
  switch (res_code) {
    case 200:
      return "200 OK";
    case 201:
      return "201 Created";
    case 202:
      return "202 Accepted";
    case 204:
      return "202 Accepted";
    case 301:
      return "301 Moved Permanently";
    case 302:
      return "302 Found";
    case 400:
      return "400 Bad Request";
    case 401:
      return "401 Unauthorized";
    case 403:
      return "403 Forbidden";
    case 404:
      return "404 Not Found";
    case 405:
      return "405 Method Not Allowed";
    case 409:
      return "409 Conflict";
    case 429:
      return "429 Too Many Requests";
    case 500:
      return "500 Internal Server Error";
    case 502:
      return "502 Bad Gateway";
    case 503:
      return "503 Service Unavailable";
    default:
      return res_code + " HTTP";
  }
};

const formatFloat = (value, decimals = 1) => {
  let retVal = parseFloat(value).toFixed(decimals);
  retVal = +retVal;
  return retVal;
};

const getUserRoleString = (userType) => {
  switch (normalizeUserType(userType)) {
    case undefined:
    case null:
      return "";
    case UserType.SUPER_ADMIN:
      return "Super Administrator";
    case UserType.ADMIN:
      return "Administrator";
    case UserType.ORG_ADMIN:
      return "Organisation Administrator";
    case UserType.ORG_USER:
      return "Organisation User";
    default:
      return "Unknown user";
  }
};

const formatPeriod2HumanReadable = (period) => {
  switch (period) {
    case "30m":
      return "30 Minutes";
    case "60m":
      return "60 Minutes";
    case "1h":
      return "1 Hour";
    case "3h":
      return "3 Hours";
    case "6h":
      return "6 Hours";
    case "24h":
      return "24 Hours";
    case "1d":
      return "1 Day";
    case "3d":
      return "3 Days";
    case "7d":
      return "7 Days";
    case "1M":
      return "1 Month";
    case "3M":
      return "3 Months";
    default:
      return undefined;
  }
};

export {
  EMPTY_CELL,
  displayCell,
  formatDate,
  formatDateOnly,
  formatTimeOnly,
  getBrowserTimeZone,
  getBrowserTimeZoneOffset,
  getHumanTimeLength,
  formatBytes,
  formatNumbers,
  formatHttpResponseCode,
  formatFloat,
  getUserRoleString,
  formatPeriod2HumanReadable,
};
